import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const languages = [
  { code: 'no', flag: 'NO', label: 'Norsk', path: '/' },
  { code: 'en', flag: 'EN', label: 'English', path: '/en' },
];

// SVG Flag components for crisp rendering
function NorwayFlag({ size = 20 }) {
  return (
    <svg width={size} height={Math.round(size * 0.73)} viewBox="0 0 22 16" fill="none" className="rounded-[3px] overflow-hidden flex-shrink-0">
      <rect width="22" height="16" fill="#BA0C2F" />
      <rect x="0" y="6" width="22" height="4" fill="#FFFFFF" />
      <rect x="6" y="0" width="4" height="16" fill="#FFFFFF" />
      <rect x="0" y="6.75" width="22" height="2.5" fill="#00205B" />
      <rect x="6.75" y="0" width="2.5" height="16" fill="#00205B" />
    </svg>
  );
}

function UKFlag({ size = 20 }) {
  return (
    <svg width={size} height={Math.round(size * 0.6)} viewBox="0 0 60 36" fill="none" className="rounded-[3px] overflow-hidden flex-shrink-0">
      <rect width="60" height="36" fill="#00247D" />
      <path d="M0 0L60 36M60 0L0 36" stroke="white" strokeWidth="6" />
      <path d="M0 0L60 36M60 0L0 36" stroke="#CF142B" strokeWidth="2" />
      <rect x="24" y="0" width="12" height="36" fill="white" />
      <rect x="0" y="12" width="60" height="12" fill="white" />
      <rect x="26" y="0" width="8" height="36" fill="#CF142B" />
      <rect x="0" y="14" width="60" height="8" fill="#CF142B" />
    </svg>
  );
}

export function FlagIcon({ code, size = 20 }) {
  if (code === 'no') return <NorwayFlag size={size} />;
  if (code === 'en') return <UKFlag size={size} />;
  return null;
}

export default function LanguageSelector() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const current = location.pathname.startsWith('/en') ? 'en' : 'no';
  const currentLang = languages.find(l => l.code === current);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const switchTo = (lang) => {
    setOpen(false);
    if (lang.code === 'no') {
      // If on /en/privacy -> /personvern, /en -> /
      if (location.pathname === '/en/privacy') navigate('/personvern');
      else navigate('/');
    } else {
      // If on /personvern -> /en/privacy, / -> /en
      if (location.pathname === '/personvern') navigate('/en/privacy');
      else navigate('/en');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 h-9 sm:h-10 px-2.5 sm:px-3 rounded-[10px] sm:rounded-[12px] border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-[background-color,border-color] duration-180"
        data-testid="language-selector-button"
      >
        <FlagIcon code={current} size={18} />
        <span className="text-xs sm:text-sm text-white/70 font-medium hidden sm:inline">{currentLang?.label}</span>
        <svg className="w-3 h-3 text-white/30" fill="none" viewBox="0 0 12 12"><path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-40 rounded-[12px] bg-[#121212] border border-white/10 py-1.5 shadow-xl z-50 overflow-hidden">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => switchTo(lang)}
              data-testid={`language-option-${lang.code}`}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm transition-[background-color,color] duration-180 ${
                lang.code === current
                  ? 'text-[#CCFF00] bg-[#CCFF00]/5'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <FlagIcon code={lang.code} size={20} />
              <span className="font-medium">{lang.label}</span>
              {lang.code === current && (
                <svg className="w-3.5 h-3.5 ml-auto text-[#CCFF00]" fill="none" viewBox="0 0 16 16"><path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
